import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../../../lib/api";
import { DashHero, DashKpis, RevenueTrend, TodayList, QuickTiles } from "./DashTop";
import { TodayBoard } from "./TodayBoard";
import { LiveFleet } from "./LiveFleet";
import { IncomeChart, MonthSummary, Deadlines } from "./Panels";
import { romeDay, incomeEntries, sumBy } from "./util";

const minsNow = () => { const [h, m] = new Date().toLocaleTimeString("it-IT", { timeZone: "Europe/Rome", hour: "2-digit", minute: "2-digit", hour12: false }).split(":").map(Number); return h * 60 + m; };

export function MarbleBoard({ user, tools, weather, expirations, exec }) {
  const [d, setD] = useState({ bookings: [], payments: [], customers: [], drivers: [], vehicles: [] });
  const today = romeDay();
  const [day, setDay] = useState(today);
  const load = useCallback(async () => {
    const r = await Promise.allSettled([api.get("/bookings"), api.get("/payments", { params: { include_voided: false } }), api.get("/customers"), api.get("/drivers"), api.get("/vehicles")]);
    const v = (i) => (r[i].status === "fulfilled" ? r[i].value.data || [] : []);
    setD({ bookings: v(0), payments: v(1), customers: v(2), drivers: v(3), vehicles: v(4) });
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 120000); return () => clearInterval(t); }, [load]);

  const m = useMemo(() => {
    const cust = Object.fromEntries(d.customers.map(c => [c.id, c.name]));
    const drv = Object.fromEntries(d.drivers.map(x => [x.id, `${x.first_name || ""} ${x.last_name || ""}`.trim()]));
    const veh = Object.fromEntries(d.vehicles.map(x => [x.id, x]));
    const todays = d.bookings.filter(b => b.date === today);
    const op = todays.filter(b => !["Cancellata", "Preventivo"].includes(b.status));
    const running = todays.filter(b => b.status === "In corso");
    const inService = new Set(running.map(b => b.vehicle_id).filter(Boolean));
    const unav = d.vehicles.filter(v => v.status && v.status !== "disponibile");
    const entries = incomeEntries(d.bookings, d.payments);
    const month = today.slice(0, 7);
    const due = d.bookings.filter(b => (b.date || "").startsWith(month) && b.status !== "Cancellata" && ["Da incassare", "Parzialmente incassato"].includes(b.payment_status))
      .reduce((s, b) => s + Number(b.price || 0), 0);
    const drvOfVeh = (v) => drv[v.current_driver_id] || drv[(running.find(b => b.vehicle_id === v.id) || {}).driver_id] || drv[(v.assigned_driver_ids || [])[0]] || "";
    const conflicts = new Set((exec?.planning?.lines || []).flatMap(l => l.blocks.filter(b => b.conflict).map(b => b.id)));
    return {
      names: { customer: (b) => cust[b.customer_id] || b.passenger_name || "—", driver: (id) => drv[id] || "", plate: (id) => veh[id]?.plate || "" },
      today: { count: op.length, running: running.length, todo: op.filter(b => ["Confermata", "Assegnata"].includes(b.status)).length },
      entries, incToday: sumBy(entries, x => x === today), incMonth: sumBy(entries, x => x.startsWith(month)), due,
      vehicles: { inService: inService.size, total: d.vehicles.length, unavailable: unav.length, unavailLabel: [...new Set(unav.map(v => v.status))].join(", ") },
      inService, drvOfVeh, conflicts,
    };
  }, [d, today, exec]);

  return (
    <div className="space-y-3 md:space-y-4" data-testid="mb-board">
      <DashHero tools={tools} day={day} setDay={setDay} />
      <DashKpis bookings={d.bookings} customers={d.customers} vehicles={d.vehicles} inService={day === today ? m.vehicles.inService : 0} day={day} isToday={day === today} />
      <div className="grid grid-cols-1 xl:grid-cols-[1fr_1.1fr] gap-3 md:gap-4">
        <RevenueTrend bookings={d.bookings} day={day} />
        <TodayList bookings={d.bookings} day={day} vehicleName={(id) => { const v = d.vehicles.find((x) => x.id === id); return v ? `${v.brand || ""} ${v.model || ""}`.trim() : ""; }} />
      </div>
      <QuickTiles vehicles={d.vehicles.length} drivers={d.drivers.length} incToday={m.incToday.total} />
      <div className="grid grid-cols-1 xl:grid-cols-[1.25fr_1fr] gap-3 md:gap-4">
        <TodayBoard bookings={d.bookings} day={day} setDay={setDay} isToday={day === today} nowMin={minsNow()} names={m.names} conflicts={m.conflicts} />
        <LiveFleet vehicles={d.vehicles} inService={m.inService} driverOf={m.drvOfVeh} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 md:gap-4">
        <IncomeChart entries={m.entries} today={today} />
        <MonthSummary inc={m.incMonth} due={m.due} today={today} />
        <Deadlines items={expirations} />
      </div>
    </div>
  );
}
